<template>
  <ion-page>
    <ion-tabs>
      <ion-router-outlet></ion-router-outlet>
      <ion-tab-bar slot="bottom" class="bottom-tabbar">
        <ion-tab-button
          v-for="(i, idx) in tabList"
          :key="i.tab"
          :class="{ 'active-class': currentIndex === idx }"
          :tab="i.tab"
          :href="i.path"
          @click="changeBtn(idx, $event)"
        >
          <div v-show="currentIndex === idx" class="checked-icon">
            <img class="pic" :src="i.clickedIcon" alt="" />
          </div>
          <img v-show="currentIndex !== idx" class="pic" :src="i.icon" alt="" />
          <ion-label class="label">{{ i.label }}</ion-label>
        </ion-tab-button>
      </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<script setup lang="ts">
import { IonLabel, IonPage, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from "@ionic/vue"
import { computed, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { checkUserType } from "@/js/message"
import { getBrandImageUrl } from "@/js/brandImages.ts"
import store from "@/js/store"

type TabItem = {
  tab: string
  path: string
  label: string
  icon: string
  clickedIcon: string
}

/** platformFunction：空=仅首页；含2=自习室；含1=首页；1和2都有=都显示 */
function parsePlatformFunction(raw: unknown): Set<number> {
  const set = new Set<number>()
  if (raw == null || raw === "") return set
  const list = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(/[,，\s]+/)
        .filter(Boolean)
  list.forEach((v) => {
    const n = Number(v)
    if (n === 1 || n === 2) set.add(n)
  })
  return set
}

function getTabVisibility() {
  const user = store.getCurrentUser?.() || {}
  const funcs = parsePlatformFunction(user?.platformFunction)
  const has1 = funcs.has(1)
  const has2 = funcs.has(2)
  // 为空：仅首页；含1=首页；含2=自习室
  return {
    showHome: !has1 && !has2 ? true : has1,
    showStudyRoom: has2,
  }
}

const allTabs: TabItem[] = [
  {
    tab: "teacher-home",
    path: "/TeacherIndex",
    label: "首页",
    icon: getBrandImageUrl("tabbar/home_icon.png"),
    clickedIcon: getBrandImageUrl("tabbar/home_active_icon.png"),
  },
  {
    tab: "teacher-study-room",
    path: "/StudyRoom",
    label: "自习室",
    icon: getBrandImageUrl("tabbar/stu_mine.png"),
    clickedIcon: getBrandImageUrl("tabbar/stu_mine_active.png"),
  },
  {
    tab: "teacher-record",
    path: "/ClassRecord",
    label: "训练记录",
    icon: getBrandImageUrl("tabbar/record_icon.png"),
    clickedIcon: getBrandImageUrl("tabbar/record_active_icon.png"),
  },
  {
    tab: "teacher-student",
    path: "/StudentList",
    label: "学员信息",
    icon: getBrandImageUrl("tabbar/student_icon.png"),
    clickedIcon: getBrandImageUrl("tabbar/student_active_icon.png"),
  },
  {
    tab: "teacher-mine",
    path: "/TeacherPersonalIndex",
    label: "个人中心",
    icon: getBrandImageUrl("tabbar/mine_icon.png"),
    clickedIcon: getBrandImageUrl("tabbar/mine_active_icon.png"),
  },
]

const currentIndex = ref(0)
const route = useRoute()
const router = useRouter()

const tabList = computed(() => {
  const { showHome, showStudyRoom } = getTabVisibility()
  return allTabs.filter((item) => {
    if (item.tab === "teacher-home") return showHome
    if (item.tab === "teacher-study-room") return showStudyRoom
    return true
  })
})

const pathList = computed(() => tabList.value.map((item) => item.path))

function changeBtn(type: number, event?: Event) {
  if (checkUserType()) {
    event?.preventDefault()
    event?.stopPropagation()
    return
  }
  // 已在当前 tab，阻止重复跳转
  if (currentIndex.value === type) {
    event?.preventDefault()
    return
  }
  currentIndex.value = type
}

function ensureVisibleRoute(path: string) {
  const { showHome, showStudyRoom } = getTabVisibility()
  if (path === "/TeacherIndex" && !showHome && showStudyRoom) {
    router.replace("/StudyRoom")
    return
  }
  if (path === "/StudyRoom" && !showStudyRoom && showHome) {
    router.replace("/TeacherIndex")
  }
}

watch(
  () => route.path,
  (newVal) => {
    ensureVisibleRoute(newVal)
    const idx = pathList.value.indexOf(newVal)
    if (idx >= 0) {
      currentIndex.value = idx
    }
  },
  { immediate: true }
)
</script>
<style scoped>
:deep(.bottom-tabbar) {
  height: 3.75rem;
  display: flex;
  justify-content: space-around;
}

.pic {
  width: 1.375rem;
  height: 1.375rem;
  position: relative;
  z-index: 2;
}

.checked-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* .checked-icon::after {
  content: "";
  display: block;
  width: 2.36rem;
  height: 2.36rem;
  border-radius: 50%;
  background: #c38365;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 0;
} */

.label {
  color: #868f9e;
  font-size: 0.625rem;
  margin-top: 0.125rem;
}

.active-class {
  /* background-image: url("@/assets/version7/bottom_checked_bg.svg"); */
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.active-class .label {
  font-weight: 600;
  color: var(--theme-color);
}

.active-class .checked-icon {
  /* margin-bottom: 0.3rem; */
}

.active-class::part(native) {
  /* padding: 0 0 0.3rem; */
}

ion-tab-button {
  margin-top: 0.4rem;
}

ion-tab-button::part(native) {
  /* padding: 0 0 0.3rem; */
}

ion-router-outlet {
  /* height: calc(100vh - 4.6rem); */
}

ion-tab-bar {
  overflow: visible;
  position: relative;
  background: #fff;
  /* box-shadow: 0 -1px 1px 0 rgba(0, 0, 0, 0.05); */
}

@media screen and (min-width: 600px) {
  .label {
    font-size: 3vmin;
  }
}
</style>
